import React, { useContext } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { Form, Field } from "react-final-form";
import { BannerContext } from "context/banner";
import { LogContext } from "context/log";
import { Heading } from "@pepdirect/ui/heading";
import { Button } from "@pepdirect/ui/button";
import { FieldInput } from "@pepdirect/ui/FieldInput";
import { FieldSelect } from "@pepdirect/ui/FieldSelect";
import { Checkbox } from "@pepdirect/ui/checkbox";
import { useSendGetStartedEmailMutation } from "services/graphql/generated";
import { stateOptions } from "@pepdirect/shared/constants";
import {
  getStartedFields,
  businessTypeOptions,
  numberOfUsersOptions,
  manageAndOrderOptions,
} from "constants/getStarted";
import { ReactSelectDropdownOption } from "@pepdirect/ui/dropdown/types";
import { validateWithYup } from "@pepdirect/ui/validation";
import { getStartedSchema } from "helpers/validation";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import st from "./getStarted.module.scss";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import {
  generatePageId,
  logGetStartedFormSubmit,
} from "@pepdirect/helpers/analyticsLogger";

interface GetStartedFormValues {
  firstName: string;
  lastName: string;
  emailField: string;
  businessName: string;
  businessAddress: string;
  city: string;
  state: ReactSelectDropdownOption;
  zip: string;
  phoneNumber: string;
  businessType: ReactSelectDropdownOption;
  manageAndOrder: ReactSelectDropdownOption;
  numberOfUsers: ReactSelectDropdownOption;
  existingCustomer: boolean;
}

export default function GetStarted(): JSX.Element {
  const { setBannerDetails } = useContext(BannerContext);
  const { useLogPageView } = useContext(LogContext);
  const [sendGetStartedEmail, { error, loading }] =
    useSendGetStartedEmailMutation();

  const title = `Get started on ${META_TITLE}`;
  useLogPageView({
    pageTitle: `get started - ${META_TITLE}`,
    pageCategory: undefined,
    pageType: PAGE_TYPE_ENUM.contact,
    id: generatePageId("get-started"),
  });

  const router = useRouter();

  const onSubmit = async (values: GetStartedFormValues) => {
    const { businessType, numberOfUsers, manageAndOrder, state } = values;
    const input = {
      ...values,
      businessType: businessType.value,
      numberOfUsers: numberOfUsers.value,
      manageAndOrder: manageAndOrder.value,
      state: state.value,
    };
    logGetStartedFormSubmit(
      "quote",
      Object.keys(input).length,
      "submit quote request",
      "body",
      window.location.hostname + "/v/request-quote-confirmation-success"
    );
    try {
      await sendGetStartedEmail({
        variables: { input },
      });

      router.push("/").then(() => {
        setBannerDetails({
          displayText:
            "Thank you for submitting! A sales rep will contact you soon.",
          type: "success",
          autoDismiss: true,
        });
      });
    } catch (e) {
      // logged in errorLink
    }
  };

  return (
    <div className={st.wrapper}>
      <Head>
        <title>{title}</title>
        <link rel="canonical" href={`${CANONICAL_URL_BASE}/get-started`} />
      </Head>

      <Heading level="h1" size="xxl" center className={st.topHeading}>
        Get Started
      </Heading>
      <Heading level="h2" size="s" center className={st.secondaryHeading}>
        Complete this form and an expert sales representative will be in touch
        with additional information.
      </Heading>

      <Form
        onSubmit={onSubmit}
        validate={validateWithYup(getStartedSchema)}
        render={({ handleSubmit, valid }) => (
          <form onSubmit={handleSubmit}>
            <div className={st.fields}>
              <div className={st.row}>
                <FieldInput
                  label={getStartedFields.firstName.label}
                  name={getStartedFields.firstName.name}
                  autoComplete={getStartedFields.firstName.autoComplete}
                />
                <FieldInput
                  label={getStartedFields.lastName.label}
                  name={getStartedFields.lastName.name}
                  autoComplete={getStartedFields.lastName.autoComplete}
                />
              </div>
              <FieldInput
                label={getStartedFields.email.label}
                name={getStartedFields.email.name}
                autoComplete={getStartedFields.email.autoComplete}
              />
              <FieldInput
                label={getStartedFields.businessName.label}
                name={getStartedFields.businessName.name}
              />
              <FieldInput
                label={getStartedFields.phone.label}
                name={getStartedFields.phone.name}
              />
              <FieldInput
                label={getStartedFields.street.label}
                name={getStartedFields.street.name}
              />
              <div className={st.cityStateZip}>
                <FieldInput
                  label={getStartedFields.city.label}
                  name={getStartedFields.city.name}
                />
                <FieldSelect
                  label={getStartedFields.state.label}
                  name={getStartedFields.state.name}
                  options={stateOptions}
                  data-testid={getStartedFields.state.label}
                />
                <FieldInput
                  label={getStartedFields.zip.label}
                  name={getStartedFields.zip.name}
                />
              </div>
              <FieldSelect
                label={getStartedFields.type.label}
                name={getStartedFields.type.name}
                options={businessTypeOptions}
                data-testid={getStartedFields.type.label}
              />
              <FieldSelect
                label={getStartedFields.numUsers.label}
                name={getStartedFields.numUsers.name}
                options={numberOfUsersOptions}
                data-testid={getStartedFields.numUsers.label}
              />
              <FieldSelect
                label={getStartedFields.manageAndOrder.label}
                name={getStartedFields.manageAndOrder.name}
                options={manageAndOrderOptions}
                data-testid={getStartedFields.manageAndOrder.label}
              />
              <div className={st.checkbox}>
                <Field
                  name={getStartedFields.isExistingCustomer.name}
                  type="checkbox"
                  initialValue={false}
                >
                  {({ input }) => (
                    <div>
                      <label className={st.checkboxLabel}>
                        <Checkbox
                          checked={!!input.checked}
                          onChange={input.onChange}
                        />
                        <span className={st.checkboxText}>
                          I am an existing PepsiCo customer
                        </span>
                      </label>
                    </div>
                  )}
                </Field>
              </div>
            </div>

            <div className={st.submit}>
              <Button htmlType="submit" disabled={!valid} loading={loading}>
                Submit quote request
              </Button>
              {error && (
                <span className={st.error}>
                  An error has occured, please try again later.
                </span>
              )}
            </div>
          </form>
        )}
      />
    </div>
  );
}
